/*
 * @fileOverView Analytics mixin definition.
 */

import axios from 'axios';
import moment from 'moment';
import Cookies from 'js-cookie';

export default {
  data: function() {
    return {
      formData: {},
      formElements: null,
      subscriptionData: {}
    }
  },

  mounted() {
    this.getDocumentReferrer() && Cookies.set('referrer', this.getDocumentReferrer());
    if (this.initAnalytics) {
      this.initAnalytics();
    }
  },

  methods: {
    /**
     * Pushes an analytics event to the data layer.
     *
     * @param {String} name - The name of the event being pushed.
     * @param {Object} data - The event data to push.
     */
    pushAnalyticsEvent(name, data) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        ...data,
        "event": name
      });
    },

    /**
     * Sets the base form data to pass within all relevant events.
     *
     * @param {Object} data - The base data to pass.
     */
    setFormAnalyticsData(data) {
      this.formData = data;
    },

    /**
     * Sets the base subscription data to pass within all relevant events.
     *
     * @param {Object} data - The base data to pass.
     */
    setSubscriptionAnalyticsData(data) {
      this.subscriptionData = data;
    },

    /**
     * Attaches a callback to all form field changes.
     *
     * @param {Object} form - The form element to listen for changes in.
     * @param {Object} callback - The function to call when changes are made.
     */
    attachFormStartedEvent(form, callback) {
      this.formElements = form.querySelectorAll('input, textarea, select, button');
      for (let i = 0; i < this.formElements.length; i++) {
        this.formElements[i].addEventListener('focus', callback);
      }
    },

    /**
     * Detaches a callback to all form field changes.
     *
     * Needed because the event should only be fired once.
     *
     * @param {Object} callback - The function that was called by the listener.
     */
    detachFormStartedEvent(callback) {
      for (let i = 0; i < this.formElements.length; i++) {
        this.formElements[i].removeEventListener('focus', callback);
      }
    },

    /**
     * Populates hidden form fields used for analytics purposes.
     *
     * Typically called upon submission to ensure the values are available.
     *
     * @param {Object} form - The form to populate fields within.
     */
    populateHiddenFormFields(form) {
      const trackingCode = Cookies.get('mjff_tracking') || "{}";
      let parsedValue = {};

      try {
        parsedValue = JSON.parse(trackingCode);
      } catch (e) {}

      const values = [
        {
          'property': 'digital_source_referrer',
          'value': Cookies.get('referrer') || ""
        },
        {
          'property': 'digital_source_tracking_code',
          'value': parsedValue.value || ""
        },
        {
          'property': 'web_form_name',
          'value': this.formData.formName
        }
      ];

      // Fields to be populated should either have an ID that matches the
      // property above or, in the case of forms whose markup is harder to
      // control (such as Form Assembly), have their value set to it.
      for (let i = 0; i < values.length; i++) {
        const set = values[i];
        const field = form.querySelector(`#${set.property}`) || form.querySelector(`input[value='${set.property}']`);

        if (field && set.value) {
          field.value = set.value;
        }
        else if (field) {
          field.value = '';
        }
      }
    },

    /**
     * Gets Document Referrer.
     *
     * @return  string - The document referrer if external.
     */
    getDocumentReferrer() {
      const referrerDomain = document.referrer.split('/')[2];
      if (!referrerDomain) {
        return '';
      }

      const referrerURL = new URL(document.referrer);
      const referrerRootDomain = referrerURL.hostname;

      if (referrerRootDomain !== location.hostname && Cookies.get('referrer') !== referrerRootDomain) {
        return document.referrer;
      }

      return '';
    },

    /**
     * Populates the hidden Donate form IP field used for analytics purposes.
     *
     * Called earlier than populateHiddenDonateFields() because it doesn't rely
     * on any other scripts and must make its own request. Calling it during
     * submission would be too late.
     *
     * @param {Object} form - The form to populate fields within.
     */
    populateHiddenDonateIpField(form) {
      axios({method: 'get', url: '/donate/getIp'})
        .then(response => {
          const field = form.querySelector('input[name="CustomerIP"]');
          const clientIp = response.data[0];

          if (field && clientIp) {
            field.value = clientIp;
          }
        })
        .catch(() => {});
    },

    /**
     * Pushes the Form Started event.
     *
     * @param {Object} event - The 'change' event.
     */
    pushFormStartedEvent(event) {
      const data = {
        "form": {
          ...this.formData,
          "formField": [
            {
              "fieldID": event.target.id
            }
          ]
        }
      };

      this.pushAnalyticsEvent('Form Started', data);
      this.detachFormStartedEvent(this.pushFormStartedEvent);
    },

    /**
     * Pushes the Donation Completed event.
     *
     * @param {Object} data - The donation data to report.
     */
    pushDonationCompletedEvent(data) {
      this.pushAnalyticsEvent('Donation Completed', {
        "donation": data
      })
    },

    /**
     * Pushes the Donation Started event.
     *
     * @param {Object} event - The 'change' event.
     */
    pushDonationStartedEvent(event) {
      this.pushAnalyticsEvent('Donation Started', {
        "donation": {
          "donationType": this.donationType
        }
      });
      this.detachFormStartedEvent(this.pushDonationStartedEvent);
    },

    /**
     * Pushes the Email Subscription Started event.
     */
    pushEmailSubscriptionStartedEvent() {
      this.pushAnalyticsEvent('Email Subscription Started', {
        "subscription": this.subscriptionData
      });
      this.detachFormStartedEvent(this.pushEmailSubscriptionStartedEvent);
    },

    /**
     * Pushes the Email Subscription Succeeded event.
     */
    pushEmailSubscriptionCompletedEvent() {
      this.pushAnalyticsEvent('Email Subscription Completed', {
        "subscription": this.subscriptionData
      });
    },

    /**
     * Pushes the Form Viewed event.
     */
    pushFormViewedEvent() {
      this.pushAnalyticsEvent('Form Viewed', {
        "form": this.formData
      });
    },

    /**
     * Pushes the Form Submission Succeeded event.
     */
    pushFormSubmissionSucceededEvent() {
      this.pushAnalyticsEvent('Form Submission Succeeded', {
        "form": this.formData
      });
    },

    /**
     * Pushes the Form Submission Failed event.
     *
     * Called for individual form fields that didn't pass validation.
     *
     * @param {Object} errorMap - The errors found by the validator.
     */
    pushFormValidationFailedEvent(errorMap) {
      let formField = [];

      // Translate the errorMap into the array expected by the analytics.
      for (const id in errorMap) {
        if (errorMap.hasOwnProperty(id)) {
          formField.push({
            "formFieldError": errorMap[id],
            "fieldId": id
          });
        }
      }

      this.pushAnalyticsEvent('Form Submission Failed', {
        "form": {
          ...this.formData,
          "formField": formField
        }
      });
    },

    /**
     * Pushes the Form Submission Failed event.
     *
     * Called for form-wide errors.
     *
     * @param {String} errorMessage - The error message reported by the form.
     */
    pushFormSubmissionFailedEvent(errorMessage) {
      this.pushAnalyticsEvent('Form Submission Failed', {
        "form": {
          ...this.formData,
          "formError": errorMessage
        }
      });
    },

    /**
     * Pushes the Page Loaded event.
     *
     * This is rarely used. The Page Loaded event, and the data for it, are
     * handled by server-side code within the MJFF Analytics module. This method
     * exists for situations where that event should be triggered again due to
     * user action, such as switching tabs on the Donate page. Anything calling
     * this method is responsible for changing window.mjffPageData beforehand.
     */
    pushPageLoadedEvent() {
      this.pushAnalyticsEvent('Page Loaded', {
        "page": window.mjffPageData
      });
    },

    /**
     * Pushes the Podcast Started event.
     *
     * @param {String} podcastName - The name of the podcast being played.
     */
    pushPodcastStartedEvent(podcastName) {
      this.pushAnalyticsEvent('Podcast Started', {
        "podcast": {
          "podcastName": podcastName
        }
      });
    },

    /**
     * Pushes the Podcast Subscription Clicked event.
     *
     * @param {String} podcastName - The name of the podcast.
     * @param {String} podcastName - The medium being clicked.
     */
    pushPodcastSubscriptionClickedEvent(podcastName, medium) {
      this.pushAnalyticsEvent('Podcast Subscription Clicked', {
        "podcast": {
          "podcastName": podcastName,
          "podcastMedium": medium
        }
      });
    },

    /**
     * Pushes the Search Results Clicked event.
     *
     * @param {String} title - The cleaned title of the listing clicked.
     * @param {String} position - The index of the listing clicked.
     */
    pushSearchResultsClickedEvent(title, position) {
      this.pushAnalyticsEvent('Search Results Clicked', {
        "listing": {
          "listingResults": {
            "item": [
              {
                "itemName": title,
                "itemPosition": position
              }
            ]
          }
        }
      });
    },

    /**
     * Pushes the Search Results Listing Viewed event.
     *
     * @param {String} term -  The searched term.
     * @param {String} order - The order the results are sorted by.
     * @param {String} count - The number of results.
     */
    pushSearchResultsListingViewedEvent(term, order, count) {
      this.pushAnalyticsEvent('Search Results Listing Viewed', {
        "listing": {
          "listingResults": {
            "resultsCount": count
          },
          "listingParams": {
            "sorts": [
              {
                "sortOrder": order
              }
            ],
            "searchInfo": {
              "searchTermEntered": term
            }
          }
        }
      });
    },

    /**
     * Pushes the Social Shared event.
     *
     * @param {String} network
     *   The network being shared from.
     * @param {String} type
     *   The type of share:
     *   - 'organization' for footer links
     *   - 'page:<pageName>' for page shares
     */
    pushSocialSharedEvent(network, type) {
      this.pushAnalyticsEvent('Social Shared', {
        "social": {
          "socialNetwork": network,
          "socialShareType": type
        }
      });
    },

    /**
     * Pushes the Webinar Registration Started event.
     *
     * @param {String} webinarId
     *   The ID of the webinar being registered for.
     */
    pushWebinarRegistrationStartedEvent(webinarId) {
      this.pushAnalyticsEvent('Webinar Registration Started', {
        "webinar": {
          "webinarID": webinarId
        }
      });
    },

    /**
     * Pushes the Webinar Registration Completed event.
     *
     * @param {String} webinarId
     *   The ID of the webinar being registered for.
     */
    pushWebinarRegistrationCompletedEvent(webinarId) {
      this.pushAnalyticsEvent('Webinar Registration Completed', {
        "webinar": {
          "webinarID": webinarId
        }
      });
    }
  }
};
